/** Section 4 — "The Strategic and Operational Role of HOLOL in the Global Economic Media Initiative" (EN PDF). */
export const operationalRoleEn = {
  id: 'operational-role',
  previewCount: 5,
  navLabel: "HOLOL's role",
  eyebrow: 'Strategic & operational role',
  title: 'The Strategic and Operational Role of HOLOL in the Global Economic Media Initiative',
  subtitle:
    'A moving bridge between national visions and international markets — using media, relationships, events, and meetings to open markets and develop opportunities.',
  blocks: [
    {
      icon: 'campaign',
      title: 'Introduction',
      wide: true,
      paragraphs: [
        "HOLOL, through the Global Economic Media Initiative, represents an integrated model that brings together media, economics, investment, relationships, and market access — using media in a way that goes beyond the traditional presentation of projects and news to create new channels for presenting countries' visions, economies, and opportunities, and connecting them with markets, investors, companies, and institutions capable of engaging with them.",
        "The concept is built around a system that operates across multiple markets, so that a country's vision and opportunities are not confined to its domestic market, but can be presented in other markets, reaching new stakeholders and creating dialogues, relationships, and opportunities that can be developed in coordination with the relevant entities.",
        'Media remains the primary interface and a central tool within this model, supported by specialized activities involving file development, connecting relevant parties, developing relationships and opportunities, and organizing specialized events, meetings, and exhibitions.',
      ],
    },
    {
      icon: 'add_business',
      title: 'What HOLOL Adds to Governments and Institutions',
      paragraphs: [
        'Governments already possess their own institutions, media outlets, relationships, and resources. However, maintaining continuous and structured access to international markets requires a different type of platform.',
        "This is where HOLOL creates additional value: taking a country's vision and opportunities from its domestic market into other markets through an organized economic and media framework.",
        "Instead of relying only on a local economic program to present the country's projects, HOLOL can take that vision into other markets and connect it with investors, companies, banks, and institutions that may be able to participate in those opportunities.",
      ],
      closing:
        'In this way, the country becomes present not only before its domestic audience, but also within a growing network of international markets, relationships, and opportunities.',
    },
    {
      icon: 'checklist',
      title: "Transforming the Country's Vision into an Action Agenda",
      paragraphs: [
        "HOLOL begins by understanding the country's vision and economic and investment objectives, the sectors it seeks to develop, the projects it wants to promote, and the needs for which it is seeking investors, companies, financing, expertise, or partnerships.",
        'It then works, in coordination with the relevant entities, to transform this vision into a set of files that can be:',
      ],
      flow: [
        'Presented through media',
        'Marketed',
        'Taken to the appropriate markets',
        'Connected with relevant parties',
        'Developed into opportunities',
      ],
      closing:
        'A file may relate to an entire sector, a specific project, an asset, an auction, a tender, an investment opportunity, financing, an international partnership, or the entry of a company or product into a new market.',
    },
    {
      icon: 'storefront',
      title: 'Marketing the Country Across Multiple Markets',
      paragraphs: [
        'The concept of country marketing is one of the core elements of the HOLOL model. This does not simply mean promoting the image of a country — it means presenting its economic and investment vision, sectors, opportunities, and needs to markets that can add value to them.',
        'If a country is targeting investors from a particular market, its message can be taken to that market through a program, meeting, event, exhibition, or specialized media campaign. If it is targeting multiple markets, the same opportunity or file can move across several markets instead of remaining confined to the domestic market.',
      ],
      closing:
        "As HOLOL expands from a limited number of markets into a broader international network, the country's ability to reach multiple entities, markets, and opportunities through one system expands accordingly.",
    },
    {
      icon: 'sync_alt',
      title: 'Connecting Countries, Markets, Investors, and Companies',
      paragraphs: [
        'HOLOL works to create two-way movement between markets. If a country needs an investor, company, financial institution, technology, expertise, or strategic partnership, HOLOL works to identify suitable parties in other markets.',
        'Conversely, if an investor, company, or institution is looking for a project, opportunity, or new market, HOLOL can connect it with the appropriate market and relevant entity.',
        'This connectivity is not limited to investors. It also includes:',
      ],
      chips: [
        'Banks & financial institutions',
        'Major companies',
        'Developers',
        'Technology companies',
        'Advisory institutions',
        'Service providers',
        'Specialized expertise',
      ],
      closing:
        'In this way, companies and institutions benefit from the ecosystem alongside governments and public-sector entities.',
    },
    {
      icon: 'museum',
      title: 'International Exhibitions and Specialized Country Opportunity Events',
      wide: true,
      paragraphs: [
        'Alongside the general international exhibition that can bring together participants within the HOLOL ecosystem, a more specialized model can also be developed: international exhibitions dedicated to the opportunities of a specific country.',
        "For example, an international exhibition can be organized around investment and business opportunities in a particular country, presenting its sectors, projects, and investment opportunities to investors, companies, banks, and international institutions. The exhibition does not have to be limited to booths and presentations. It can include:",
      ],
      bullets: [
        'Project and opportunity presentations.',
        'Economic and investment sessions.',
        'One-to-one meetings between investors and opportunity owners.',
        'Closed-door rooms for specialized files.',
        'Meetings with financial institutions and companies.',
        'Media coverage and programs associated with the event.',
        'Follow-up on opportunities generated through the exhibition.',
      ],
      closing: [
        "The exhibition can also be developed into a travelling international series, moving from one market to another and presenting the country's opportunities in the markets it seeks to reach.",
        "In this way, the event becomes more than a country simply participating in an international exhibition — it becomes a specialized platform for marketing the country's opportunities in external markets.",
      ],
    },
    {
      icon: 'gavel',
      title: 'Auctions, Tenders, and Economic Opportunities',
      wide: true,
      paragraphs: [
        'HOLOL can use media, relationships, and international markets to support practical files with direct economic value.',
      ],
      labeled: [
        {
          label: 'Auctions',
          text: "Where a country or institution has assets, projects, or opportunities that can be offered through an auction, HOLOL can help expand the auction to international buyers and investors, in coordination with the competent entity responsible for the file. HOLOL's role may include assessing the target market, reaching suitable parties, designing the media and marketing presence of the auction, and supporting access to potential buyers.",
        },
        {
          label: 'Tenders and opportunities',
          text: 'Where a tender or project requires a company, technology, financing, or an international partner, the opportunity can be presented in markets where capable parties are located, and connected to the relevant decision-making entities.',
        },
        {
          label: 'Investment and financing',
          text: 'Investment opportunities and financing requirements can also be presented to suitable investors, financial institutions, and markets, facilitating dialogue between them and the relevant opportunity owners.',
        },
        {
          label: 'Cross-border exchange of opportunities',
          text: 'Models can also be developed for exchanging opportunities, assets, projects, and partnerships between companies and institutions in different countries, according to the nature of each file and its legal and commercial framework.',
        },
      ],
    },
    {
      icon: 'rocket_launch',
      title: 'Media as a Driving Force for the Project',
      paragraphs: [
        'Media within HOLOL is not a separate layer added after the economic work. It is the tool that makes that work visible, accessible, scalable, and capable of generating engagement. Through media, the initiative can:',
      ],
      flow: [
        'Present the vision',
        'Present the opportunity',
        'Build interest',
        'Reach relevant parties',
        'Open dialogue',
        'Support relationships',
        'Highlight outcomes',
      ],
      closing: [
        'The process also works in the opposite direction: relationships, opportunities, transactions, and events that develop beyond the screen can become new media stories and economic content.',
        'In this way, programs, media content, exhibitions, and meetings become part of one continuous cycle.',
      ],
    },
    {
      icon: 'meeting_room',
      title: 'Closed-Door Rooms and Specialized Meetings',
      paragraphs: [
        'When the nature of a file requires greater focus and privacy, specialized meetings or closed-door rooms can be organized for the parties directly connected to the opportunity. Depending on the nature of the file, these meetings may include:',
      ],
      chips: [
        'Government officials',
        'Investors',
        'Financial institutions',
        'Specialized companies',
        'Project owners',
        'International partners',
      ],
      flowLabel: 'The objective is to move from general presentation to discussion of:',
      flow: [
        'The need',
        'Possible solutions',
        'Relevant parties',
        'Cooperation mechanism',
        'Next step',
      ],
      flowJoin: '+',
      closing:
        'Not all details need to be broadcast. Some discussions may continue privately according to the nature of the opportunity and the agreement of the parties involved.',
    },
    {
      icon: 'moving',
      title: 'Developing Opportunities After Media Exposure',
      paragraphs: [
        "HOLOL's role does not end when a program or event ends. An opportunity may begin through a television program, exhibition, or meeting and then require follow-up and coordination to move it to a more advanced stage. Accordingly, some files can progress through:",
      ],
      flow: [
        'Media presentation',
        'Interest',
        'Contact',
        'Meeting',
        'Opportunity development',
        'Negotiation',
        'Cooperation, project, or transaction',
      ],
      closing:
        'Conversely, an opportunity may begin outside the media environment and later be brought into the media when appropriate. This makes media part of a continuous value-creation cycle, rather than an isolated product.',
    },
    {
      icon: 'corporate_fare',
      title: "HOLOL's Role with Companies and Institutions",
      paragraphs: [
        'The concept of country marketing does not exclude companies. On the contrary, companies and institutions are essential to the movement created by the initiative. When a country opens a market or presents an opportunity, it needs:',
      ],
      flow: [
        'Investors',
        'Companies',
        'Banks',
        'Experts',
        'Technologies',
        'Services',
        'Partners',
      ],
      flowJoin: '+',
      closing: [
        "Companies participating in the initiative can therefore benefit from access to new opportunities, international markets, institutional relationships, media exposure, meetings, and events, while the country benefits from their capabilities, expertise, products, and investments.",
        "In this way, the country's vision, the market, the company, and the investor become interconnected parts of one ecosystem.",
      ],
    },
    {
      icon: 'category',
      title: 'Development of the Ecosystem and Its Specializations',
      paragraphs: [
        'As HOLOL expands, the model develops through multiple specialized areas and functions dealing with different types of files, including:',
      ],
      chips: [
        'Investment',
        'Projects',
        'Auctions',
        'Valuation',
        'Financing',
        'Markets',
        'International relationships',
      ],
      closing:
        "The purpose of these specializations is to strengthen the economic and media work and provide greater capacity to handle different files, without losing the project's fundamental identity as an international economic media initiative.",
    },
    {
      icon: 'route',
      title: 'The Integrated Model',
      wide: true,
      paragraphs: ["HOLOL's role can be summarized in one continuous path:"],
      flowVariant: 'path',
      flow: [
        'Country vision',
        'Objectives, needs, and opportunities',
        'Organization of files and identification of pathways',
        'Media and marketing of the vision and opportunities',
        'Access to relevant markets',
        'Investors, companies, and institutions',
        'Meetings, exhibitions, and specialized rooms',
        'Relationship and opportunity development',
        'Projects, partnerships, investments, and transactions — according to the nature of each file',
      ],
      closing:
        'With every new market HOLOL enters, the circle of parties and opportunities that can be connected across the broader network expands.',
    },
    {
      icon: 'flag',
      title: 'Conclusion',
      wide: true,
      highlight: true,
      paragraphs: [
        "HOLOL's role is not to replace governments or institutions, nor is it simply to produce a traditional media program. Rather, it is to build a moving bridge between national visions and international markets, using media, relationships, events, meetings, and specialized tools to open markets and develop opportunities.",
        'A country presents its vision and objectives, and HOLOL helps transform them into files that can be marketed, accessed, and developed. It then uses its media platform, relationships, and access to different markets to reach investors, companies, institutions, and other parties capable of participating. At the same time, companies, institutions, and investors benefit from the same ecosystem by gaining access to new markets, opportunities, and relationships.',
        'Thus, the HOLOL model is based on one fundamental idea: to make media a means of opening markets, marketing national visions, connecting economies, and developing relationships and opportunities across borders.',
      ],
    },
  ],
};
